import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, Printer, Download, Upload, AlertTriangle, RefreshCw, Baby, Users, Camera } from "lucide-react";
import QRCode from "qrcode";
import { PublicLayout } from "../components/PublicLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { DigitalCard } from "../components/DigitalCard";
import { TransferAccountsCard } from "../components/TransferAccountsCard";
import { api, apiError, money, fileUrl } from "../lib/api";
import { StatusBadge, RegistrationSummary, GroupSummary } from "./ConfirmacionPage";

const MAX_RECEIPT_MB = 5;
const RECEIPT_TYPES = ["image/jpeg", "image/png", "application/pdf"];
const UPLOAD_STATES = ["pending", "rejected", "correction_requested", "receipt_uploaded"];
const CONFIRMED_STATES = ["approved", "waived", "imported_confirmed"];
const PAYMENT_STATE_TEXT = {
  cancelled: "Registro cancelado",
  pending: "Pendiente de pago",
  receipt_uploaded: "Comprobante recibido; está en revisión",
  rejected: "Comprobante rechazado",
  correction_requested: "Se requiere corrección del comprobante",
  approved: "Pago confirmado",
  waived: "Pago confirmado",
  imported_confirmed: "Pago confirmado",
};

const CareQrCard = ({ token }) => {
  const [qr, setQr] = useState(null);
  useEffect(() => {
    let active = true;
    QRCode.toDataURL(token, { width: 240, margin: 1, color: { dark: "#2F557F", light: "#FFFFFF" } })
      .then((url) => active && setQr(url))
      .catch(() => {});
    return () => { active = false; };
  }, [token]);
  return (
    <div className="overflow-hidden rounded-2xl border-2 border-hz-turquoise/50 bg-white shadow-sm" data-testid="care-qr-card">
      <div className="bg-hz-navy px-6 py-4 text-center">
        <p className="text-xs font-bold uppercase tracking-widest text-hz-turquoise">QR familiar de cuidado infantil</p>
        <p className="mt-1 text-lg font-extrabold text-white">Horizonte 2026</p>
      </div>
      <div className="flex flex-col items-center px-6 py-6">
        {qr ? <img src={qr} alt="QR familiar de cuidado infantil" className="h-44 w-44 rounded-lg" data-testid="care-qr-image" /> : <div className="h-44 w-44 animate-pulse rounded-lg bg-hz-gray/30" />}
        <p className="mt-4 text-center text-xs leading-relaxed text-hz-ink/60">
          Preséntalo en el área de cuidado infantil para registrar la entrada o salida de tus niños. La entrega se confirma individualmente con la cuidadora.
        </p>
      </div>
    </div>
  );
};

const ConsultaPage = () => {
  const navigate = useNavigate();
  const [folio, setFolio] = useState("");
  const [contact, setContact] = useState("");
  const [contactType, setContactType] = useState("email");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [groupResult, setGroupResult] = useState(null);
  const [queue, setQueue] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);
  const [uploadErr, setUploadErr] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  useEffect(() => {
    document.title = "Consulta de registro — Horizonte 2026";
  }, []);

  const isGroupFolio = /^HZ26-(GRP|FAM)-/i.test(folio.trim());

  const refresh = async () => {
    if (groupResult) {
      const payload = { group_folio: folio.trim() };
      if (contactType === "email") payload.email = contact.trim(); else payload.phone = contact.trim();
      const { data } = await api.post("/registrations/group-lookup", payload);
      setGroupResult(data);
    } else if (result) {
      const payload = { folio: result.folio };
      if (contactType === "email") payload.email = contact.trim(); else payload.phone = contact.trim();
      const { data } = await api.post("/registrations/lookup", payload);
      setResult(data);
    }
  };

  const search = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setGroupResult(null);
    setQueue([]);
    setUploadMsg(null);
    setUploadErr(null);
    try {
      if (isGroupFolio) {
        const payload = { group_folio: folio.trim() };
        if (contactType === "email") payload.email = contact.trim(); else payload.phone = contact.trim();
        const { data } = await api.post("/registrations/group-lookup", payload);
        setGroupResult(data);
      } else {
        const payload = { folio: folio.trim() };
        if (contactType === "email") payload.email = contact.trim(); else payload.phone = contact.trim();
        const { data } = await api.post("/registrations/lookup", payload);
        setResult(data);
      }
    } catch (err) {
      setError(apiError(err, "No pudimos encontrar tu registro. Verifica los datos."));
    } finally {
      setLoading(false);
    }
  };

  const addFiles = (files) => {
    setUploadMsg(null);
    setUploadErr(null);
    const incoming = Array.from(files || []);
    if (!incoming.length) return;
    const next = [...queue];
    for (const file of incoming) {
      if (!RECEIPT_TYPES.includes(file.type)) {
        setUploadErr(`Formato no permitido en ${file.name}. Usa JPG, PNG o PDF.`);
        continue;
      }
      if (file.size > MAX_RECEIPT_MB * 1024 * 1024) {
        setUploadErr(`${file.name} excede ${MAX_RECEIPT_MB} MB.`);
        continue;
      }
      next.push(file);
    }
    setQueue(next);
  };

  const removeFromQueue = (idx) => setQueue(queue.filter((_, i) => i !== idx));

  const uploadReceipt = async () => {
    if (!queue.length || uploading) return;
    setUploading(true);
    setUploadErr(null);
    setUploadMsg(null);
    const failed = [];
    let successCount = 0;
    for (const file of queue) {
      try {
        const fd = new FormData();
        if (groupResult) {
          fd.append("group_folio", folio.trim());
          fd.append(contactType === "email" ? "email" : "phone", contact.trim());
          fd.append("source", "public_lookup");
          fd.append("receipt", file);
          await api.post("/registrations/group-receipt", fd);
        } else {
          fd.append("folio", result.folio);
          fd.append(contactType === "email" ? "email" : "phone", contact.trim());
          fd.append("source", "public_lookup");
          fd.append("receipt", file);
          await api.post("/registrations/receipt", fd);
        }
        successCount += 1;
      } catch (err) {
        failed.push({ name: file.name, msg: apiError(err, "No se pudo subir.") });
      }
    }
    try { await refresh(); } catch { /* best-effort */ }
    setQueue(failed.length ? queue.filter((f) => failed.some((x) => x.name === f.name)) : []);
    if (successCount > 0 && failed.length === 0) {
      setUploadMsg(successCount === 1
        ? "Comprobante recibido. El equipo organizador lo revisará."
        : `${successCount} comprobantes recibidos. El equipo organizador los revisará.`);
    } else if (successCount > 0 && failed.length > 0) {
      setUploadErr(`Se subieron ${successCount} archivo(s). Falló: ${failed.map((f) => `${f.name} (${f.msg})`).join("; ")}`);
    } else {
      setUploadErr(failed[0]?.msg || "No se pudo subir el comprobante. Intenta de nuevo.");
    }
    setUploading(false);
  };

  const activeData = groupResult || result;
  const paymentStatus = groupResult ? groupResult.payment_mode : result?.payment_status;
  const activeFolio = groupResult ? groupResult.group_folio : result?.folio;
  const paymentInfo = groupResult?.payment || result?.payment || {};
  const totalEsperado = groupResult ? groupResult.total_amount : result?.total_estimated;
  const outstanding = paymentInfo.outstanding_amount_cents || 0;
  const canUpload = UPLOAD_STATES.includes(paymentStatus) || outstanding > 0;
  const uploadLabel = paymentStatus === "correction_requested" ? "Subir comprobante corregido" : paymentStatus === "rejected" ? "Subir nuevo comprobante" : outstanding > 0 && !UPLOAD_STATES.includes(paymentStatus) ? "Agregar comprobante del pago pendiente" : "Subir comprobante de pago";

  return (
    <PublicLayout>
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 sm:py-14">
        <p className="text-xs font-semibold uppercase tracking-widest text-hz-blue">Consulta</p>
        <h1 className="mt-2 text-2xl font-bold tracking-tight text-hz-navy sm:text-3xl" data-testid="consulta-title">
          Consultar mi registro
        </h1>
        <p className="mt-2 text-sm text-hz-ink/70">
          Ingresa tu folio individual o el folio de tu familia o grupo (HZ26-FAM-… / HZ26-GRP-…) junto con el correo o teléfono del contacto principal.
        </p>

        <form onSubmit={search} className="mt-8 space-y-5 rounded-2xl border border-hz-gray/40 bg-white p-6 shadow-sm sm:p-8" data-testid="lookup-form">
          <div>
            <Label htmlFor="folio" className="font-semibold text-hz-ink">Folio *</Label>
            <Input id="folio" data-testid="lookup-folio-input" value={folio} onChange={(e) => setFolio(e.target.value)} placeholder="Ej. HZ26-000123, HZ26-FAM-000123 o HZ26-GRP-000123" className="mt-1.5" required />
          </div>
          <div>
            <Label className="font-semibold text-hz-ink">{isGroupFolio ? "Contacto principal (familia o grupo)" : "Verifica tu identidad con"}</Label>
            <div className="mt-2 flex gap-2">
              <button type="button" data-testid="contact-type-email" onClick={() => setContactType("email")}
                className={`flex-1 rounded-lg border-2 px-3 py-2 text-sm font-semibold transition-colors ${contactType === "email" ? "border-hz-cyan bg-hz-cyan/5 text-hz-navy" : "border-hz-gray/50 text-hz-ink/60"}`}>
                Correo electrónico
              </button>
              <button type="button" data-testid="contact-type-phone" onClick={() => setContactType("phone")}
                className={`flex-1 rounded-lg border-2 px-3 py-2 text-sm font-semibold transition-colors ${contactType === "phone" ? "border-hz-cyan bg-hz-cyan/5 text-hz-navy" : "border-hz-gray/50 text-hz-ink/60"}`}>
                Teléfono
              </button>
            </div>
            <Input data-testid="lookup-contact-input" type={contactType === "email" ? "email" : "tel"} value={contact} onChange={(e) => setContact(e.target.value)}
              placeholder={contactType === "email" ? "tucorreo@ejemplo.com" : "Ej. 55 1234 5678"} className="mt-3" required />
          </div>
          <Button type="submit" disabled={loading} data-testid="lookup-submit-btn" className="w-full bg-hz-navy font-bold text-white hover:bg-hz-blue disabled:opacity-60">
            {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Buscando…</>) : (<><Search className="mr-2 h-4 w-4" /> Consultar registro</>)}
          </Button>
          {error && (
            <p className="rounded-lg border border-hz-error/30 bg-hz-error/5 px-3 py-2 text-sm font-medium text-hz-error" data-testid="lookup-error" role="alert">{error}</p>
          )}
        </form>

        {activeData && (
          <div className="mt-8 space-y-6" data-testid="lookup-result">
            {groupResult ? <GroupSummary data={groupResult} notice="El comprobante de pago confirma el total del grupo y activa los accesos seleccionados de cada integrante." /> : <RegistrationSummary data={result} />}

            {groupResult && groupResult.members.filter((m) => m.qr_token).length > 0 && (
              <div className="space-y-4" data-testid="group-qr-cards">
                <p className="text-sm font-bold text-hz-navy">Pases de acceso de los adultos</p>
                {groupResult.members.filter((m) => m.qr_token).map((m) => (
                  <DigitalCard key={m.id} data={m} />
                ))}
              </div>
            )}
            {!groupResult && result.qr_token && <DigitalCard data={result} />}

            {groupResult?.family_care_qr_token && <CareQrCard token={groupResult.family_care_qr_token} />}

            <div className="rounded-xl border border-hz-gray/40 bg-white" data-testid="payment-card">
              <div className="flex items-center justify-between border-b border-hz-gray/40 px-4 py-3">
                <p className="text-sm font-bold text-hz-navy">Estado del pago</p>
                <button type="button" onClick={refresh} data-testid="payment-refresh-btn" className="flex items-center gap-1 text-xs font-bold text-hz-blue hover:underline">
                  <RefreshCw className="h-3 w-3" /> Actualizar estado
                </button>
              </div>
              <div className="space-y-2 px-4 py-4 text-sm">
                <div className="flex justify-between"><span className="text-hz-ink/60">Estado</span><span className="font-bold text-hz-navy" data-testid="payment-status-text">{PAYMENT_STATE_TEXT[paymentStatus] || paymentStatus}</span></div>
                <div className="flex justify-between"><span className="text-hz-ink/60">Total a pagar</span><span className="font-extrabold text-hz-navy" data-testid="payment-total-expected">{totalEsperado != null ? `${money(totalEsperado)} MXN` : "Se compartirá próximamente"}</span></div>
                {paymentInfo.uploaded_at && (
                  <div className="flex justify-between"><span className="text-hz-ink/60">Comprobante cargado</span><span className="font-semibold text-hz-ink" data-testid="payment-uploaded-at">{new Date(paymentInfo.uploaded_at).toLocaleString("es-MX")}</span></div>
                )}
                <div className="flex justify-between"><span className="text-hz-ink/60">Referencia sugerida</span><span className="font-extrabold text-hz-navy">{activeFolio}</span></div>
                {outstanding > 0 && (
                  <div className="flex justify-between rounded-lg bg-hz-warning/10 px-2 py-1" data-testid="payment-outstanding">
                    <span className="font-semibold text-hz-ink">Saldo pendiente</span>
                    <span className="font-extrabold text-hz-warning" data-testid="payment-outstanding-amount">${(outstanding / 100).toLocaleString("es-MX", {minimumFractionDigits: 2, maximumFractionDigits: 2})} MXN</span>
                  </div>
                )}
              </div>
              {CONFIRMED_STATES.includes(paymentStatus) && outstanding > 0 && (
                <p className="mx-4 mb-3 rounded-lg border-2 border-hz-warning/50 bg-hz-warning/10 px-3 py-2 text-xs font-semibold text-hz-ink" data-testid="payment-partial-notice">
                  Pago parcialmente aprobado: tus accesos activos ya están disponibles arriba, pero aún queda saldo por cubrir. Sube el comprobante del pago restante para completar los accesos pendientes.
                </p>
              )}
              {canUpload && (
                <div className="border-t border-hz-gray/40 px-4 py-4" data-testid="transfer-instructions">
                  <TransferAccountsCard accounts={(groupResult ? groupResult.payment : result?.payment)?.transfer_accounts} folio={activeFolio} testId="consulta-transfer-accounts" />
                </div>
              )}
              {(paymentStatus === "rejected" || paymentStatus === "correction_requested") && paymentInfo.review_notes && (
                <p className="mx-4 mb-4 rounded-lg border border-hz-warning/40 bg-hz-warning/5 px-3 py-2 text-xs font-medium text-hz-ink" data-testid="payment-rejection-notes">
                  Nota del revisor: {paymentInfo.review_notes}
                </p>
              )}
            </div>

            {canUpload && (
              <div className="rounded-xl border-2 border-dashed border-hz-cyan/50 bg-hz-cyan/5 p-5" data-testid="receipt-upload-section">
                <p className="text-sm font-bold text-hz-navy">{paymentStatus === "receipt_uploaded" ? "Agregar otro comprobante" : uploadLabel}</p>
                <p className="mt-1 text-xs text-hz-ink/60">Puedes adjuntar varios comprobantes para respaldar tu pago. JPG, PNG o PDF — máximo {MAX_RECEIPT_MB} MB por archivo.</p>
                <p className="mt-1 text-xs text-hz-ink/50">En algunos dispositivos, la cámara puede abrirse mediante el selector del sistema.</p>
                {(paymentInfo.receipts || []).length > 0 && (
                  <div className="mt-3 rounded-lg border border-hz-gray/40 bg-white" data-testid="receipts-received-list">
                    <p className="border-b border-hz-gray/40 px-3 py-2 text-xs font-bold text-hz-navy">Comprobantes recibidos ({paymentInfo.receipts.length})</p>
                    <ul className="divide-y divide-hz-gray/30 text-xs">
                      {paymentInfo.receipts.map((r) => (
                        <li key={r.id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`receipt-received-${r.version_number}`}>
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-hz-ink" title={r.original_filename}>#{r.version_number} · {r.original_filename}</p>
                            <p className="text-[11px] text-hz-ink/60">{new Date(r.uploaded_at).toLocaleString("es-MX")}</p>
                          </div>
                          {r.download_url && (
                            <a href={r.download_url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-xs font-bold text-hz-blue hover:underline" data-testid={`receipt-view-${r.version_number}`}>Ver</a>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="receipt-camera-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,application/pdf" className="hidden" data-testid="receipt-file-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button type="button" variant="outline" onClick={() => cameraRef.current?.click()} data-testid="receipt-camera-btn" className="font-bold">
                    <Camera className="mr-2 h-4 w-4" /> Tomar foto
                  </Button>
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="receipt-pick-btn" className="font-bold">
                    <Upload className="mr-2 h-4 w-4" /> Subir archivos
                  </Button>
                </div>
                {queue.length > 0 && (
                  <ul className="mt-3 space-y-1 rounded-lg border border-hz-gray/40 bg-white p-2 text-xs" data-testid="consulta-queue">
                    {queue.map((f, idx) => (
                      <li key={`${f.name}-${idx}`} className="flex items-center justify-between gap-2" data-testid={`consulta-queue-item-${idx}`}>
                        <span className="truncate font-semibold text-hz-ink" title={f.name}>{f.name} <span className="font-normal text-hz-ink/50">({(f.size / 1024).toFixed(0)} KB)</span></span>
                        <button type="button" onClick={() => removeFromQueue(idx)} className="shrink-0 text-xs font-bold text-hz-error hover:underline" data-testid={`consulta-queue-remove-${idx}`}>Quitar</button>
                      </li>
                    ))}
                  </ul>
                )}
                <Button type="button" onClick={uploadReceipt} disabled={!queue.length || uploading} data-testid="receipt-upload-btn" className="mt-3 w-full bg-hz-cyan font-bold text-white hover:bg-hz-blue disabled:opacity-60">
                  {uploading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Subiendo…</>) : `Enviar ${queue.length > 1 ? queue.length + " comprobantes" : "comprobante"}`}
                </Button>
                {uploadErr && (
                  <p className="mt-2 flex items-start gap-2 text-xs font-medium text-hz-error" data-testid="receipt-upload-error" role="alert">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {uploadErr}
                  </p>
                )}
              </div>
            )}

            {!canUpload && (paymentInfo.receipts || []).length > 0 && (
              <div className="rounded-xl border border-hz-gray/40 bg-white" data-testid="receipts-received-list">
                <p className="border-b border-hz-gray/40 px-4 py-3 text-sm font-bold text-hz-navy">Comprobantes recibidos ({paymentInfo.receipts.length})</p>
                <ul className="divide-y divide-hz-gray/30 text-sm">
                  {paymentInfo.receipts.map((r) => (
                    <li key={r.id} className="flex items-center justify-between gap-2 px-4 py-2" data-testid={`receipt-received-${r.version_number}`}>
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-hz-ink">#{r.version_number} · {r.original_filename}</p>
                        <p className="text-xs text-hz-ink/60">{new Date(r.uploaded_at).toLocaleString("es-MX")}</p>
                      </div>
                      {r.download_url && (
                        <a href={r.download_url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-xs font-bold text-hz-blue hover:underline" data-testid={`receipt-view-${r.version_number}`}>Ver</a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {paymentStatus === "receipt_uploaded" && (
              <div className="rounded-xl border border-hz-cyan/40 bg-hz-cyan/5 px-4 py-3 text-center text-sm font-semibold text-hz-blue" data-testid="receipt-in-review">
                Tu comprobante está en revisión. Recibirás confirmación cuando el equipo organizador lo apruebe.
              </div>
            )}

            {CONFIRMED_STATES.includes(paymentStatus) && (
              <div className="rounded-xl border border-hz-success/30 bg-hz-success/5 px-4 py-3 text-center text-sm font-semibold text-hz-success" data-testid="payment-confirmed-note">
                Pago confirmado. Tus accesos activos aparecen arriba.
              </div>
            )}

            {uploadMsg && (
              <div className="rounded-xl border border-hz-success/30 bg-hz-success/5 px-4 py-3 text-center text-sm font-semibold text-hz-success" data-testid="receipt-upload-success">
                {uploadMsg}
              </div>
            )}

            <div className="flex flex-col gap-3 sm:flex-row print:hidden">
              <Button variant="outline" onClick={() => window.print()} data-testid="print-summary-btn" className="flex-1 border-hz-navy font-bold text-hz-navy hover:bg-hz-navy hover:text-white">
                <Printer className="mr-2 h-4 w-4" /> Imprimir / Guardar PDF
              </Button>
              <Button variant="outline" onClick={() => { setResult(null); setGroupResult(null); setFolio(""); setContact(""); }} data-testid="new-search-btn" className="flex-1 font-bold text-hz-ink">
                <RefreshCw className="mr-2 h-4 w-4" /> Nueva consulta
              </Button>
              {result?.qr_token && (
                <Button variant="outline" onClick={() => navigate(`/credencial/${result.qr_token}`)} data-testid="view-credential-btn" className="flex-1 font-bold text-hz-ink">
                  <Download className="mr-2 h-4 w-4" /> Ver credencial
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </PublicLayout>
  );
};

export default ConsultaPage;
